// Google Analytics tracking utility
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Track a Google Analytics event
 * @param eventName - The event name
 * @param params - Optional parameters for the event
 */
export const trackGAEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// ── Google Ads ───────────────────────────────────────────────────────────────
// GA4-Events sind KEINE Google-Ads-Conversions. Ohne `send_to` mit Conversion-
// Label landet ein gtag-Event nur im Remarketing, nie in der Conversion-Spalte,
// und die Kampagne kann nicht darauf optimieren (Befund A1, Audit 17.09.2026).

/** Google-Ads-Container — identisch zu Hundetagesstaette.tsx / GassiServiceA.tsx. */
export const GOOGLE_ADS_ID = "AW-17560289148";

/** Conversion-Aktion „Schnuppertag gebucht (Website)" (angelegt 18.09.2026):
 *  Kategorie „Lead-Formular senden", primär, eine Conversion pro Klick,
 *  Klick-Fenster 90 Tage, Standardwert 38 EUR.
 *  Gegenstück zu GOOGLE_ADS_HUTA_WARTELISTE in Hundetagesstaette.tsx — der
 *  Gassi-Funnel bekommt bewusst ein eigenes Ziel, damit Gassi- und HuTa-
 *  Kampagnen getrennt optimieren können. */
export const GOOGLE_ADS_SCHNUPPERTAG = "AW-17560289148/AzPICMb59vscEPz-srVB";

/** Standardwert der Conversion-Aktion in EUR — im Konto identisch hinterlegt. */
export const GOOGLE_ADS_SCHNUPPERTAG_WERT = 38;

/**
 * Telefonnummer als E.164 (+49…) für Google-Ads-Erweiterte-Conversions;
 * null, wenn unbrauchbar. Google verwirft jedes andere Format stillschweigend.
 * (Bewusste Kopie des Helpers aus Hundetagesstaette.tsx — die Seite exportiert
 * ihn nicht; hier liegt er an der Stelle, an der ihn weitere Seiten finden.)
 */
export function telefonE164(raw: string): string | null {
  let p = raw.replace(/[\s\-/().]/g, "");
  if (p.startsWith("00")) p = "+" + p.slice(2);
  else if (p.startsWith("0")) p = "+49" + p.slice(1);
  else if (!p.startsWith("+")) p = "+49" + p;
  return /^\+\d{8,15}$/.test(p) ? p : null;
}

// Pre-defined event helpers for common conversions

/**
 * Track Schnuppertag (trial day) booking
 */
export const trackSchnuppertagBooking = (params?: { dog_name?: string; postal_code?: string }) => {
  trackGAEvent('schnuppertag_booking', {
    event_category: 'booking',
    event_label: 'Schnuppertag',
    ...params,
  });
};

/**
 * Track subscription checkout initiation
 */
export const trackSubscriptionCheckout = (params?: { plan_type?: string; value?: number }) => {
  trackGAEvent('begin_checkout', {
    event_category: 'subscription',
    event_label: params?.plan_type,
    value: params?.value,
    currency: 'EUR',
    ...params,
  });
};

/**
 * Track subscription purchase completion
 */
export const trackSubscriptionPurchase = (params?: { plan_type?: string; value?: number }) => {
  trackGAEvent('purchase', {
    event_category: 'subscription',
    event_label: params?.plan_type,
    value: params?.value,
    currency: 'EUR',
    transaction_id: `sub_${Date.now()}`,
    ...params,
  });
};

/**
 * Track adventure pass purchase
 */
export const trackPassPurchase = (params?: {
  pass_type?: string;
  dog_size?: string;
  value?: number;
  /** Stabile Kauf-ID (z. B. die Stripe-Session). Ohne sie zählt GA4 jeden Reload
   *  der Erfolgsseite als weiteren Kauf, weil `pass_${Date.now()}` jedes Mal neu ist. */
  transaction_id?: string;
}) => {
  trackGAEvent('purchase', {
    event_category: 'pass',
    event_label: `${params?.pass_type}_${params?.dog_size}`,
    value: params?.value,
    currency: 'EUR',
    transaction_id: `pass_${Date.now()}`,
    ...params,
  });
};

/**
 * Track Regeltermin (regular appointment) booking
 */
export const trackRegelterminBooking = (params?: { dog_name?: string; date?: string }) => {
  trackGAEvent('regeltermin_booking', {
    event_category: 'booking',
    event_label: 'Regeltermin',
    ...params,
  });
};

/**
 * Track newsletter signup
 */
export const trackNewsletterSignup = () => {
  trackGAEvent('newsletter_signup', {
    event_category: 'engagement',
    event_label: 'Newsletter',
  });
};

/**
 * Track contact form submission
 */
export const trackContactForm = () => {
  trackGAEvent('contact_form', {
    event_category: 'engagement',
    event_label: 'Contact',
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName: string, location?: string) => {
  trackGAEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_location: location,
  });
};

/**
 * Track page-specific content views
 */
export const trackContentView = (contentName: string, contentType?: string) => {
  trackGAEvent('view_content', {
    event_category: 'content',
    content_name: contentName,
    content_type: contentType,
  });
};
